import { useEffect, useRef } from 'react'

import { brandAssetUrl } from '@/runtime'

/** Favicon/tab state for the chat the user is currently viewing. */
export type FaviconState = 'default' | 'thinking' | 'question'

interface MapFaviconArgs {
  enabled: boolean
  hasOpenChat: boolean
  isResponding: boolean
  hasQuestion: boolean
}

/**
 * Pure mapping from the open chat's live signals to a favicon state. Kept
 * separate from the DOM/canvas side so it can be unit-tested directly.
 *
 * Priority: a pending question beats in-progress thinking.
 */
export function mapFaviconState({
  enabled,
  hasOpenChat,
  isResponding,
  hasQuestion,
}: MapFaviconArgs): FaviconState {
  if (!enabled || !hasOpenChat) return 'default'
  if (hasQuestion) return 'question'
  if (isResponding) return 'thinking'
  return 'default'
}

const BADGE_COLORS: Record<Exclude<FaviconState, 'default'>, string> = {
  thinking: '#fff047',
  question: '#f5b400',
}

const TITLE_PREFIX: Record<FaviconState, string> = {
  default: '',
  thinking: '• ',
  question: '? ',
}

interface UseChatFaviconArgs {
  /** Only the primary chat surface should drive the tab (default false). */
  enabled?: boolean
  /** Id of the open chat, or null when no real thread is open. */
  threadId: string | null
  /** The open chat's run is in progress. */
  isResponding: boolean
  /** The open chat has paused to ask the user something. */
  hasQuestion: boolean
}

/**
 * Reflects the currently-open chat's state in the browser tab: a badged
 * favicon plus a title prefix. Background chats are handled separately by the
 * sidebar indicators; this hook is only about the open chat.
 */
export function useChatFavicon({
  enabled = false,
  threadId,
  isResponding,
  hasQuestion,
}: UseChatFaviconArgs): void {
  const baseImageRef = useRef<HTMLImageElement | null>(null)
  const baseTitleRef = useRef<string | null>(null)
  const originalIconHrefRef = useRef<string | null>(null)

  useEffect(() => {
    if (!enabled) return undefined

    if (baseTitleRef.current === null) baseTitleRef.current = document.title

    const link = ensureIconLink()
    if (originalIconHrefRef.current === null) {
      originalIconHrefRef.current = link.getAttribute('href')
    }

    const baseHref = brandAssetUrl('credem-mark.png')
    let cancelled = false

    const render = () => {
      const state = mapFaviconState({
        enabled,
        hasOpenChat: Boolean(threadId),
        isResponding,
        hasQuestion,
      })

      const baseTitle = baseTitleRef.current ?? document.title
      document.title = `${TITLE_PREFIX[state]}${baseTitle}`

      if (state === 'default') {
        link.setAttribute('href', baseHref)
      } else {
        paintBadgedIcon(link, baseImageRef.current, baseHref, BADGE_COLORS[state])
      }
    }

    // Load the base mark once, then (re)render.
    if (baseImageRef.current) {
      render()
    } else {
      const img = new Image()
      img.onload = () => {
        if (cancelled) return
        baseImageRef.current = img
        render()
      }
      img.onerror = () => {
        if (!cancelled) render()
      }
      img.src = baseHref
      // Render immediately too so default state is correct before load.
      render()
    }

    return () => {
      cancelled = true
    }
  }, [enabled, threadId, isResponding, hasQuestion])

  // Restore the plain tab when the controller unmounts or is disabled.
  useEffect(() => {
    if (enabled) return undefined
    return () => undefined
  }, [enabled])

  useEffect(() => {
    return () => {
      if (baseTitleRef.current !== null) document.title = baseTitleRef.current
      const href = originalIconHrefRef.current
      if (href) {
        const link = document.querySelector<HTMLLinkElement>('link[rel="icon"]')
        link?.setAttribute('href', href)
      }
    }
  }, [])
}

function ensureIconLink(): HTMLLinkElement {
  let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]')
  if (!link) {
    link = document.createElement('link')
    link.rel = 'icon'
    document.head.appendChild(link)
  }
  return link
}

function paintBadgedIcon(
  link: HTMLLinkElement,
  baseImage: HTMLImageElement | null,
  baseHref: string,
  color: string,
): void {
  const size = 64
  const canvas = document.createElement('canvas')
  canvas.width = size
  canvas.height = size
  const ctx = canvas.getContext('2d')
  if (!ctx) {
    // jsdom / unsupported: fall back to the plain mark.
    link.setAttribute('href', baseHref)
    return
  }

  if (baseImage) ctx.drawImage(baseImage, 0, 0, size, size)

  const radius = size * 0.14
  const cx = size - radius - 2
  const cy = size - radius - 2

  // White ring for contrast against the mark.
  ctx.beginPath()
  ctx.arc(cx, cy, radius + 3, 0, Math.PI * 2)
  ctx.fillStyle = '#ffffff'
  ctx.fill()

  ctx.beginPath()
  ctx.arc(cx, cy, radius, 0, Math.PI * 2)
  ctx.fillStyle = color
  ctx.fill()

  try {
    link.setAttribute('href', canvas.toDataURL('image/png'))
  } catch {
    link.setAttribute('href', baseHref)
  }
}
